import React, { useCallback, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackBar } from '../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { userInformationAtom } from '../../Services/_states/auth.js';
import { WebhookAction } from '../../Services/_actions/Webhook/webhook.action.js';
import DynamicBreadcrumbs from '../../components/Shared/Breadcrumb/index.tsx';
import HomeIcon from '@mui/icons-material/Home';

const CreateEditForm: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const loggedInUser: any = useRecoilValue(userInformationAtom);
  const actions = WebhookAction();
  const organisationId = loggedInUser?.profile?.organisation?.organisation_id;
  const organisationName = loggedInUser?.profile?.organisation?.name;
  const location = useLocation();
  const botFromState = location.state?.bot;

  const initialForm: ChannelInfoObj = {
    app_id: '',
    app_name: '',
    channel_number: '',
    channel_name: '',
    company_id: organisationId,
    company_name: organisationName,
    webhook_url: '',
    wa_phone_number_id: '',
    wa_user_access_token: '',
    waba_id: '',
    status: false,
  };

  const backendDefaults = {
    channel: 'whatsapp',
    wa_version: 'v18.0',
    wa_recipient_type: 'individual',
    wa_messaging_product: 'whatsapp',
    source_name: 'whatsapp',
    source_id: 'WHAPP',
  };

  const [form, setForm] = useState<ChannelInfoObj>(botFromState || initialForm);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const onChange =
    (key: keyof ChannelInfoObj) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
      setErrors((prev) => ({ ...prev, [key]: '' }));
    };

  const handleRefresh = useCallback(() => {
    showSnackBar('Refresh clicked', 'info');
  }, [showSnackBar]);

  const handleSave = async () => {
    const newErrors: { [key: string]: string } = {};

    // Validate all fields (except company_id/company_name/status)
    Object.entries(form).forEach(([key, value]) => {
      if (
        key !== 'company_id' &&
        key !== 'company_name' &&
        key !== 'status' &&
        (!value || String(value).trim() === '')
      ) {
        newErrors[key] = `${key.replace(/_/g, ' ')} is required`;
      }
    });

    // Phone number: exactly 10 digits
    const phone = form.channel_number?.trim() || '';
    if (phone && !/^\d{10}$/.test(phone)) {
      newErrors['channel_number'] = 'Channel Number must be exactly 10 digits';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const payload = { ...form, ...backendDefaults };
      if (botFromState) {
        await actions.updateBotAuthentication(payload as any);
        showSnackBar('Bot Authentication updated', 'success');
        navigate(-1);
      } else {
        await actions.createBotAuthentication(payload as any);
        showSnackBar('Bot Authentication created', 'success');
        navigate(-1);
      }
      resetForm();
    } catch (error: any) {
      showSnackBar(`Error saving bot authentication: ${error.message}`, 'error');
    }
  };

  const resetForm = () => {
    setForm(initialForm);
    setErrors({});
  };

 const breadcrumbsData = [
  {
    label: (
      <IconButton color="primary" size="small">
        <HomeIcon />
      </IconButton>
    ),
    onClick: () => navigate('/'),
  },
  {
    label: 'Bot Authentication',
        onClick: () => navigate('/r/setting/bot'),
  },
  { label: botFromState ? 'Edit' : 'Create' },
];

  return (
    <Box
      sx={{
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        p: '5px',
        pb: '15px',
      }}
    >
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(event, item) => item?.onClick?.()}
      />

      <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, p: 1 }}>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader
            title={botFromState ? 'Edit Bot Authentication' : 'Create Bot Authentication'}
            subheader={
              botFromState ? 'Update the fields and save changes' : 'Fill all fields and save'
            }
            action={
              <Tooltip title="Reload list">
                <IconButton onClick={handleRefresh}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            }
          />
          <Divider />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={ <> App Name <span style={{ color: 'red' }}>*</span> </> }
                  size='small'
                  value={form.app_name}
                  onChange={onChange('app_name')}
                  fullWidth
                  error={!!errors.app_name}
                  helperText={errors.app_name}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={<>App ID <span style={{ color: 'red' }}>*</span> </>}
                  size='small'
                  value={form.app_id}
                  onChange={onChange('app_id')}
                  fullWidth
                  error={!!errors.app_id}
                  helperText={errors.app_id}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={<>Channel Number <span style={{ color: 'red' }}>*</span> </>}
                  size='small'
                  value={form.channel_number}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setForm((prev) => ({ ...prev, channel_number: value }));
                    setErrors((prev) => ({ ...prev, channel_number: '' }));
                  }}
                  fullWidth
                  error={!!errors.channel_number}
                  helperText={errors.channel_number}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={<>Channel Name <span style={{ color: 'red' }}>*</span> </>}
                  size='small'
                  value={form.channel_name}
                  onChange={onChange('channel_name')}
                  fullWidth
                  error={!!errors.channel_name}
                  helperText={errors.channel_name}
                />
              </Grid>

              {/* Company Id hidden (removed from UI) */}

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="company-name-label">Company Name</InputLabel>
                  <Select 
                    size='small'
                    labelId="company-name-label"
                    value={organisationName || ''}
                    label="Company Name"
                    disabled
                  >
                    <MenuItem value={organisationName}>{organisationName}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label={<>Wa Phone Number Id <span style={{ color: 'red' }}>*</span> </>}
                  size='small'
                  value={form.wa_phone_number_id}
                  onChange={onChange('wa_phone_number_id')}
                  fullWidth
                  error={!!errors.wa_phone_number_id}
                  helperText={errors.wa_phone_number_id}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={<>Wa Waba Id <span style={{ color: 'red' }}>*</span> </>}
                  size='small'
                  value={form.waba_id}
                  onChange={onChange('waba_id')}
                  fullWidth
                  error={!!errors.waba_id}
                  helperText={errors.waba_id}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={<>Webhook Url <span style={{ color: 'red' }}>*</span> </>}
                  size='small'
                  value={form.webhook_url}
                  onChange={onChange('webhook_url')}
                  fullWidth
                  error={!!errors.webhook_url}
                  helperText={errors.webhook_url}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={<>Wa User Access Token <span style={{ color: 'red' }}>*</span> </>}
                  size='small'
                  value={form.wa_user_access_token}
                  onChange={onChange('wa_user_access_token')}
                  fullWidth
                  error={!!errors.wa_user_access_token}
                  helperText={errors.wa_user_access_token}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.status}
                      onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.checked }))}
                    />
                  }
                  label={<Typography>{form.status ? 'Active' : 'Inactive'}</Typography>}
                />
              </Grid>
            </Grid>

            {/* Actions aligned to the right */}
            <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSave}>
                {botFromState ? 'Update' : 'Save'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default CreateEditForm;
